import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import publicApi from '@/services/publicApi';
import { Loader2 } from 'lucide-react';
import { useThemeStore } from '@/stores/themeStore';

interface SetupStatus {
  is_setup_complete: boolean;
  organization_count: number;
  admin_user_count: number;
  question_count: number;
  needs_organization: boolean;
  needs_admin: boolean;
  needs_questions: boolean;
}

const SETUP_STATUS_ENDPOINT = '/v1/setup/status';

interface SetupCheckProps {
  children: React.ReactNode;
}

/**
 * SetupCheck Component
 *
 * Checks if the system has been initialized and redirects to setup wizard if needed.
 * Should wrap the entire app routing to ensure setup is complete before allowing access.
 */
export const SetupCheck: React.FC<SetupCheckProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { resolvedTheme } = useThemeStore();
  const isDark = resolvedTheme === 'dark';

  // Public interview routes should bypass setup check entirely.
  // These include magic link entry, QR code identity capture, and public interviews.
  const isPublicInterviewRoute =
    location.pathname.startsWith('/interview/') ||
    location.pathname === '/interview/start';

  const { data: setupStatus, isLoading, error, refetch } = useQuery<SetupStatus>({
    queryKey: ['setupStatus'],
    queryFn: async () => {
      const response = await publicApi.get(SETUP_STATUS_ENDPOINT);
      return response.data;
    },
    retry: 1,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    enabled: !isPublicInterviewRoute,
  });

  useEffect(() => {
    // Don't redirect if already on setup page or any interview-related page
    const isOnSetupPage = location.pathname === '/setup';

    if (isOnSetupPage || isPublicInterviewRoute) {
      return;
    }

    // If setup is not complete, redirect to setup wizard
    if (setupStatus && !setupStatus.is_setup_complete) {
      navigate('/setup', { replace: true });
    }
  }, [setupStatus, navigate, location.pathname, isPublicInterviewRoute]);

  // Public interview routes bypass setup check -- render children immediately
  if (isPublicInterviewRoute) {
    return <>{children}</>;
  }

  // Show loading state while checking setup status
  if (isLoading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'mm-workplace mm-workplace-bg text-white' : 'mm-light mm-light-bg text-gray-900'}`}>
        <div className="text-center">
          <Loader2 className={`w-12 h-12 animate-spin mx-auto mb-4 ${isDark ? 'text-violet-400' : 'text-violet-600'}`} />
          <p className="text-white/80">Checking system status...</p>
        </div>
      </div>
    );
  }

  // Show error state if setup check failed
  if (error) {
    return (
      <div className={`min-h-screen flex items-center justify-center p-4 ${isDark ? 'mm-workplace mm-workplace-bg text-white' : 'mm-light mm-light-bg text-gray-900'}`}>
        <div className="mm-glass rounded-2xl p-8 text-center max-w-md">
          <div className="w-16 h-16 bg-rose-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-rose-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">
            Connection Error
          </h2>
          <p className="text-white/70 mb-6">
            Unable to connect to the server. Please ensure the backend is running.
          </p>
          <button
            onClick={() => void refetch()}
            className="px-6 py-2 bg-violet-500 hover:bg-violet-600 text-white rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-violet-400"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Render children if setup is complete or on allowed pages
  return <>{children}</>;
};
